import { useDeepEqualMemo } from '@carvana/merch-react-hooks';
import { useCallback, useContext } from 'react';
import dealershipFamilyMap from '~/constants/dealership/dealershipFamilyMap';
import { appVersion, isNonProd } from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import StaticContext from '~/context/common/StaticContext';
import { useUserLocation } from '~/context/common/UserContext';
import {
  useDealershipAddress,
  useDealershipConfiguration,
  useDealershipId
} from '~/context/dealership/StaticContext';
import type {
  PageViewPayload,
  VehicleDetailsPayload
} from '~/contracts/dealership/ShiftDigital';
import getShiftDigitalPageType from '~/utilities/dealership/getShiftDigitalPageType';

export default function useShiftDigitalPageViewEventTracker(
  vehicleDetails?: VehicleDetailsPayload
) {
  const { browserCookieId, eventCorrelationId } = useContext(StaticContext);
  const dealershipId = useDealershipId();
  const dealershipAddress = useDealershipAddress();
  const { fullName } = useDealershipConfiguration();
  const trackEvent = useTrackEvent();
  const userLocation = useUserLocation();
  const vehicleDetailsToUse = useDeepEqualMemo(vehicleDetails);

  return useCallback(() => {
    const pageType = getShiftDigitalPageType();
    const payload: PageViewPayload<typeof pageType> = {
      dealerBac: '',
      dealerCity: dealershipAddress.city ?? 'unknown',
      dealerName: fullName ?? 'unknown',
      dealerState: dealershipAddress.state ?? 'unknown',
      dealerTda: '',
      dealerZipCode: dealershipAddress.zip ?? 'unknown',
      language: navigator.language,
      pageBrand: dealershipId ? dealershipFamilyMap[dealershipId] : '',
      sessionId: `${browserCookieId}|${eventCorrelationId}`,
      siteTechnologyVersion: appVersion,
      userCity: userLocation.city,
      userState: userLocation.state,
      userZipCode: userLocation.zip,
      vehicleDetails: vehicleDetailsToUse,
      websiteTier: 'Tier 3'
    };

    window.sd?.('dataLayer', { ...payload, pageType });
    window.sd?.('send', 'pageview');

    trackEvent('ShiftDigital', { dealershipId, event: 'pageview', payload });

    if (isNonProd) {
      console.debug('Send to shift digital', 'pageview', payload);
    }
  }, [
    browserCookieId,
    dealershipAddress.city,
    dealershipAddress.state,
    dealershipAddress.zip,
    dealershipId,
    eventCorrelationId,
    fullName,
    trackEvent,
    userLocation.city,
    userLocation.state,
    userLocation.zip,
    vehicleDetailsToUse
  ]);
}
